'use client';

import { FC, useState, useCallback, useEffect } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/hooks/useTranslations';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateExpenseSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { FormError, FormSelect } from '@/components/form';
import { editExpense, uploadReceipt, processOCR } from '@/actions';
import { pdfFirstPageToBase64, pdfToText } from '@/lib/pdf-to-image';
import { formatDateForInput } from '@/lib/date-formatter';
import { useExpenseContext } from '@/contexts/expense-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';
import { ExpenseForEdit } from '@/types';
import { DEFAULT_EXPENSE_ITEM } from '@/lib/constants/expense';
import { ReceiptUpload } from '../receipt-upload';
import { ExpenseItems } from '../expense-items';
import { ExpenseCategory, PaymentMethod } from '@/generated/prisma/enums';
import { Loader2 } from 'lucide-react';
import { TItems } from '@/types/invoice';
import { useSession } from 'next-auth/react';

interface EditExpenseFormProps {
  expense?: ExpenseForEdit;
}

export const EditExpenseForm: FC<EditExpenseFormProps> = ({ expense }) => {
  const [error, setError] = useState<string | undefined>('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [useItems, setUseItems] = useState(false);
  const [items, setItems] = useState<TItems[]>([{ ...DEFAULT_EXPENSE_ITEM }]);

  const t = useTranslations('CreateExpensePopup');
  const common = useTranslations('Common');
  const session = useSession();

  const { status, wrap } = useSubmitStatus();

  useEffect(() => {
    if (expense?.items && expense.items.length > 0) {
      setUseItems(true);
      setItems(
        expense.items.map((item) => ({
          description: item.description,
          qty: item.qty,
          unit: item.unit,
          price: item.price,
          tax: item.tax,
          taxAmount: item.taxAmount ?? 0,
          total: item.total,
        }))
      );
    } else {
      setUseItems(false);
      setItems([{ ...DEFAULT_EXPENSE_ITEM }]);
    }
  }, [expense?.id, expense?.items]);

  const form = useForm<z.infer<typeof CreateExpenseSchema>>({
    resolver: zodResolver(CreateExpenseSchema),
    defaultValues: {
      id: expense?.id || '',
      description: expense?.description || '',
      amount: expense?.amount ? Number(expense.amount) : 0,
      taxAmount: expense?.taxAmount ? Number(expense.taxAmount) : undefined,
      date: expense?.date ? new Date(expense.date) : new Date(),
      category: expense?.category || undefined,
      vendor: expense?.vendor || '',
      invoiceNumber: expense?.invoiceNumber || '',
      paymentMethod: expense?.paymentMethod || undefined,
      receiptUrl: expense?.receiptUrl || '',
      notes: expense?.notes || '',
    },
  });

  useEffect(() => {
    if (expense) {
      form.reset({
        id: expense.id,
        description: expense.description || '',
        amount: expense.amount ? Number(expense.amount) : 0,
        taxAmount: expense.taxAmount ? Number(expense.taxAmount) : undefined,
        date: expense.date ? new Date(expense.date) : new Date(),
        category: expense.category || undefined,
        vendor: expense.vendor || '',
        invoiceNumber: expense.invoiceNumber || '',
        paymentMethod: expense.paymentMethod || undefined,
        receiptUrl: expense.receiptUrl || '',
        notes: expense.notes || '',
      });
    }
  }, [expense, form]);

  const { dispatch } = useExpenseContext();

  const categoryOptions = [
    {
      value: ExpenseCategory.OFFICE_SUPPLIES as string,
      label: t('category.officeSupplies'),
    },
    { value: ExpenseCategory.TRAVEL as string, label: t('category.travel') },
    {
      value: ExpenseCategory.UTILITIES as string,
      label: t('category.utilities'),
    },
    {
      value: ExpenseCategory.SOFTWARE as string,
      label: t('category.software'),
    },
    {
      value: ExpenseCategory.MARKETING as string,
      label: t('category.marketing'),
    },
    {
      value: ExpenseCategory.EQUIPMENT as string,
      label: t('category.equipment'),
    },
    { value: ExpenseCategory.MEALS as string, label: t('category.meals') },
    {
      value: ExpenseCategory.TRANSPORTATION as string,
      label: t('category.transportation'),
    },
    { value: ExpenseCategory.OTHER as string, label: t('category.other') },
  ];

  const paymentMethodOptions = [
    { value: PaymentMethod.CASH as string, label: t('paymentMethod.cash') },
    {
      value: PaymentMethod.CREDIT_CARD as string,
      label: t('paymentMethod.creditCard'),
    },
    {
      value: PaymentMethod.DEBIT_CARD as string,
      label: t('paymentMethod.debitCard'),
    },
    {
      value: PaymentMethod.BANK_TRANSFER as string,
      label: t('paymentMethod.bankTransfer'),
    },
    { value: PaymentMethod.PAYPAL as string, label: t('paymentMethod.paypal') },
    { value: PaymentMethod.OTHER as string, label: t('paymentMethod.other') },
  ];

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setReceiptFile(file);
      setIsProcessingOCR(true);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const uploadResult = await uploadReceipt(formData);
        if (uploadResult.error) {
          setError(uploadResult.error);
          setIsProcessingOCR(false);
          return;
        }

        if (uploadResult.success) {
          form.setValue('receiptUrl', uploadResult.success);

          const isImage = file.type.startsWith('image/');
          const isPdf = file.type === 'application/pdf';
          if (isImage || isPdf) {
            try {
              let base64: string;
              let extractedText: string | undefined;

              if (isPdf) {
                try {
                  extractedText = await pdfToText(file, 3);
                } catch {
                  /* fall back to image */
                }
                base64 = await pdfFirstPageToBase64(file);
              } else {
                base64 = await fileToBase64(file);
              }

              const ocrResult = await processOCR(base64, extractedText);
              if (ocrResult.amount) form.setValue('amount', ocrResult.amount);
              if (ocrResult.taxAmount)
                form.setValue('taxAmount', ocrResult.taxAmount);
              if (ocrResult.date) form.setValue('date', ocrResult.date);
              if (ocrResult.vendor) form.setValue('vendor', ocrResult.vendor);
              if (ocrResult.invoiceNumber)
                form.setValue('invoiceNumber', ocrResult.invoiceNumber);
              if (ocrResult.description)
                form.setValue('description', ocrResult.description);
              if (ocrResult.category)
                form.setValue(
                  'category',
                  ocrResult.category as ExpenseCategory
                );
              if (ocrResult.items && ocrResult.items.length > 0) {
                setUseItems(true);
                setItems(
                  ocrResult.items.map((item) => ({
                    description: item.description,
                    qty: item.qty,
                    unit: item.unit,
                    price: item.price,
                    tax: item.tax,
                    taxAmount: item.taxAmount ?? 0,
                    total: item.total,
                  }))
                );
              }
            } catch (ocrError) {
              console.error('OCR processing failed:', ocrError);
            }
          }
        }
      } catch (err) {
        console.error('File upload failed:', err);
        setError('Failed to upload receipt. Please try again.');
      } finally {
        setIsProcessingOCR(false);
      }
    },
    [form, fileToBase64]
  );

  const handleRemoveReceipt = useCallback(() => {
    setReceiptFile(null);
    form.setValue('receiptUrl', '');
  }, [form]);

  const onSubmit = (values: z.infer<typeof CreateExpenseSchema>) => {
    setError('');
    const submitValues = useItems
      ? {
          ...values,
          items: items.filter((item) => item.description.trim() !== ''),
        }
      : { ...values, items: undefined };
    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          editExpense(submitValues)
            .then((data) => {
              if (data.success) {
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_EDIT_EXPENSE_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                setError(data.error);
                reject();
              }
            })
            .catch(() => {
              setError('Something went wrong!');
              reject();
            });
        })
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {/* Receipt Upload */}
          <ReceiptUpload
            onFileSelect={handleFileSelect}
            currentFile={receiptFile}
            currentUrl={expense?.receiptUrl || null}
            onRemove={handleRemoveReceipt}
            disabled={status === 'loading' || isProcessingOCR}
          />
          {isProcessingOCR && (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('processingReceipt')}</span>
            </div>
          )}

          <div className="border-b"></div>

          {/* Mode Toggle */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-800">
            <div>
              <p className="text-sm font-medium">{t('entryMode')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {useItems ? t('itemizedModeDesc') : t('simpleModeDesc')}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setUseItems(!useItems);
                if (!useItems) {
                  form.setValue('description', '');
                  form.setValue('amount', 0);
                } else {
                  setItems([{ ...DEFAULT_EXPENSE_ITEM }]);
                }
              }}
              disabled={status === 'loading'}
            >
              {useItems ? t('switchToSimple') : t('switchToItemized')}
            </Button>
          </div>

          {useItems ? (
            <ExpenseItems
              items={items}
              setItems={setItems}
              status={status}
              currency={session.data?.user.currency}
            />
          ) : (
            <>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('description_label')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={status === 'loading'}
                        placeholder={t('descriptionPlaceholder')}
                        type="text"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('amount')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={status === 'loading'}
                          placeholder={t('amountPlaceholder')}
                          type="number"
                          step="0.01"
                          value={field.value === 0 ? '' : field.value}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === '' ? 0 : Number(value));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxAmount"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('taxAmount')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled={status === 'loading'}
                          placeholder={t('taxAmountPlaceholder')}
                          type="number"
                          step="0.01"
                          value={
                            field.value === 0 || !field.value ? '' : field.value
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(
                              value === '' ? undefined : Number(value)
                            );
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          {/* Date */}
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('date')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={status === 'loading'}
                    type="date"
                    value={
                      field.value instanceof Date
                        ? formatDateForInput(field.value)
                        : ''
                    }
                    onChange={(e) => field.onChange(new Date(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-b"></div>

          {/* Category and Payment Method */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormSelect
              control={form.control}
              name="category"
              label={t('category.label')}
              placeholder={t('category.placeholder')}
              disabled={status === 'loading'}
              options={categoryOptions}
            />

            <FormSelect
              control={form.control}
              name="paymentMethod"
              label={t('paymentMethod.label')}
              placeholder={t('paymentMethod.placeholder')}
              disabled={status === 'loading'}
              options={paymentMethodOptions}
            />
          </div>

          {/* Vendor and Invoice Number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="vendor"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('vendor')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={status === 'loading'}
                      placeholder={t('vendorPlaceholder')}
                      type="text"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="invoiceNumber"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('invoiceNumber')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={status === 'loading'}
                      placeholder={t('invoiceNumberPlaceholder')}
                      type="text"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Notes */}
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('notes')}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    disabled={status === 'loading'}
                    placeholder={t('notesPlaceholder')}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormError message={error} />

        <LoadingButton
          isFull={true}
          type="submit"
          status={status}
          idleText={common('confirm')}
        />
      </form>
    </Form>
  );
};
